import { NONE } from '@festina-life/api'
import type { ConfigurableServer, Optional } from '@festina-life/core'
import type { QueryKey } from '@tanstack/react-query'

export const fetchMetaModelKeyPrefix = ({
  server,
  branch,
}: {
  server: ConfigurableServer
  branch: Optional<string>
}): QueryKey => [`/component/${server}/${branch ?? NONE}/meta/model`]

export const fetchMetaModelKey = ({
  server,
  branch,
  layerId,
}: {
  server: ConfigurableServer
  branch: Optional<string>
  layerId: string | null
}): QueryKey => [...fetchMetaModelKeyPrefix({ server, branch }), layerId]

export const fetchBlocksKey = ({
  isFromConfigurationServer,
  server,
}: {
  isFromConfigurationServer: boolean
  server: ConfigurableServer
}): QueryKey => ['blocks', server, isFromConfigurationServer]

export const fetchLayersKey = ({
  server,
  isFromConfigurationServer,
}: {
  server: ConfigurableServer
  isFromConfigurationServer: boolean
}): QueryKey => ['layers', server, isFromConfigurationServer]

export const fetchServerTenantsKey = (server: ConfigurableServer): QueryKey => [
  `${server}/tenants`,
]

type FetchAllBranchChangesetsKeyPrefixProps = {
  componentName: ConfigurableServer
  branchName?: Optional<string>
  isFromConfigurationServer: boolean
}

export const fetchAllBranchChangesetsKeyPrefix = ({
  componentName,
  branchName,
  isFromConfigurationServer,
}: FetchAllBranchChangesetsKeyPrefixProps) => [
  'changesets',
  'tip',
  {
    branchName: branchName || NONE,
    componentName,
    isFromConfigurationServer,
  },
]

export const fetchAllBranchChangesetsKey = ({
  componentName,
  branchName,
  isFromConfigurationServer,
  includeBaseChangesets,
  shouldIncludeExtrinsicParameterValues,
}: FetchAllBranchChangesetsKeyPrefixProps & {
  includeBaseChangesets: boolean
  shouldIncludeExtrinsicParameterValues: Optional<boolean>
}): QueryKey => [
  ...fetchAllBranchChangesetsKeyPrefix({
    componentName,
    branchName,
    isFromConfigurationServer,
  }),
  `${includeBaseChangesets ? 'includingBase' : 'excludingBase'}/${shouldIncludeExtrinsicParameterValues && !isFromConfigurationServer ? 'includingExtrinsicParameterValues' : 'excludingIntrinsicParameterValues'}`,
]

export const fetchAllWorkspaceBranchChangesetsKeyPrefix = ({
  componentName,
  baseBranchName,
}: {
  componentName: ConfigurableServer
  baseBranchName: string
}): QueryKey => [
  '/changesets/workspace',
  componentName.toLocaleLowerCase(),
  baseBranchName,
]

export const fetchAllWorkspaceBranchChangesetsKey = ({
  componentName,
  workspaceName,
  baseBranchName,
}: {
  componentName: ConfigurableServer
  workspaceName: string
  baseBranchName: string
}): QueryKey => [
  ...fetchAllWorkspaceBranchChangesetsKeyPrefix({
    componentName,
    baseBranchName,
  }),
  workspaceName,
]

export const fetchStandardComponentConcisePackageKey = (
  server: ConfigurableServer,
): QueryKey => [`/${server}/changesets/concise-package`]
